import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { supabase } from '../supabaseClient';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CategoryBarGraph = () => {
  const [categoryData, setCategoryData] = useState({
    labels: [],
    datasets: []
  });
  const [activeCategory, setActiveCategory] = useState('performance');
  const [allData, setAllData] = useState([]);

  const categories = [
    'performance',
    'financial_products_services',
    'customer_service_support',
    'security_privacy',
    'rewards_promotions',
    'other'
  ];

  const categoryColors = {
    'performance': 'rgba(75, 192, 192, 0.6)',
    'financial_products_services': 'rgba(255, 99, 132, 0.6)',
    'customer_service_support': 'rgba(255, 206, 86, 0.6)',
    'security_privacy': 'rgba(54, 162, 235, 0.6)',
    'rewards_promotions': 'rgba(153, 102, 255, 0.6)',
    'other': 'rgba(255, 159, 64, 0.6)'
  };

  useEffect(() => {
    fetchCategoryData();
  }, []);

  useEffect(() => {
    if (allData.length > 0) {
      updateChartData(activeCategory);
    }
  }, [activeCategory, allData]);

  const fetchCategoryData = async () => {
    console.log('Fetching category data...');
    const { data, error } = await supabase
      .from('app_issues')
      .select('app_name, performance, financial_products_services, customer_service_support, security_privacy, rewards_promotions, other, total_reviews');

    if (error) {
      console.error('Error fetching data:', error);
    } else {
      console.log('Fetched data:', data);
      
      if (data.length === 0) {
        console.log('No data returned from the query');
        return;
      }

      const processedData = data.map(item => ({
        app_name: item.app_name,
        ...categories.reduce((acc, category) => ({
          ...acc,
          [category]: item.total_reviews > 0 ? (item[category] / item.total_reviews) * 100 : 0
        }), {})
      }));

      setAllData(processedData);
      updateChartData(activeCategory, processedData);
    }
  };

  const updateChartData = (category, data = allData) => {
    const sortedData = [...data].sort((a, b) => b[category] - a[category]);

    setCategoryData({
      labels: sortedData.map(item => item.app_name),
      datasets: [{
        label: formatCategoryName(category),
        data: sortedData.map(item => item[category].toFixed(2)),
        backgroundColor: categoryColors[category],
      }]
    });
  };

  const formatCategoryName = (category) => {
    return category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'App Issues by Category',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y + '%';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Apps'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Percentage of Issues'
        },
        max: 100  // Set the maximum value to 100%
      }
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        {categories.map(category => (
          <button 
            key={category} 
            onClick={() => handleCategoryChange(category)}
            style={{
              margin: '0 10px 10px 0',
              padding: '5px 10px',
              backgroundColor: category === activeCategory ? categoryColors[category] : 'white',
              color: category === activeCategory ? 'white' : 'black',
              border: `1px solid ${categoryColors[category]}`,
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            {formatCategoryName(category)}
          </button>
        ))}
      </div>
      {categoryData.labels.length > 0 ? (
        <Bar options={options} data={categoryData} />
      ) : (
        <div>Loading data...</div>
      )}
    </div>
  );
};

export default CategoryBarGraph;
